import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { Link, useLocation } from "wouter";
import { useHistoryState } from "wouter/use-browser-location";

import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { taskIndicatorParts } from "@/shared/taskFilterLabels.ts";
import { userTaskHref } from "@/types/userTaskLinks.ts";
import type { UserTask } from "@/types/userTasks.ts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { Button } from "@/components/ui/button.tsx";
import { type MuteChoice, muteChoices, muteConfirm } from "@/shared/muteChoices.ts";
import { CREATE_RULE_LABEL } from "@/shared/taskRowCopy.ts";
import { BellOff, MessageCircleMore, Signpost } from "lucide-react";
import { useAskAbout } from "./AskAboutTask.tsx";
import { TaskRulePanel } from "./TaskRulePanel.tsx";
import { useMuteSender } from "./useMuteSender.ts";
import { OkthxLogo } from "@/components/icons/Logo.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { toPlainText } from "@/shared/markdownPlainText.ts";
import { questionCount } from "@/shared/openQuestions.ts";
import { taskHolderLabel, taskHolderState } from "@/shared/taskHolder.ts";
import { userTaskQueries } from "./api.ts";
import { useOpenQuestions } from "./useOpenQuestions.ts";
import { sourceLinks } from "./sourceLinks.ts";
import { useSetTaskContext } from "./api.ts";
import { ContextEditor } from "./ContextEditor.tsx";
import { LabelChip } from "./LabelChip.tsx";
import { SourceMark } from "./RowMoreMenu.tsx";
import { DueLine, GroupMark, RowActions } from "./UserTaskRow.tsx";

/**
 * Whose move it is: the face of whoever has to act next, with the words
 * saying what they owe. The phone's card draws the same line from the same
 * shared decision — the order of its tests is load-bearing, which is why it
 * lives in one place.
 */
const TaskHolderLine = ({ task, questions }: { task: UserTask; questions: number }) => {
  const { userContext } = useCurrentUserContext();
  const state = taskHolderState(task, questions);
  if (!state) return null;

  const me = userContext?.user?.userContact;
  const label = taskHolderLabel(state);
  const face = () => {
    if (state.kind === "waiting") return <TaskFace name={state.name} />;
    if (state.kind === "okthx") return <OkthxLogo className="size-5 text-foreground" />;
    return <TaskFace name={me?.name ?? null} picture={me?.picture ?? undefined} />;
  };

  return (
    <p className="flex items-center gap-2 text-sm">
      {/* The face is decorative: the words beside it already say what it
          stands for, and a picture alone cannot tell "Okthx is working on it"
          from "Okthx needs your input". */}
      <span aria-hidden>{face()}</span>
      <span className={state.kind === "blocked" ? "font-medium" : "text-muted-foreground"}>
        {label}
      </span>
    </p>
  );
};

/** A small round face for the holder line. */
const TaskFace = ({ name, picture }: { name: string | null; picture?: string }) => (
  <Avatar className="size-5 shrink-0">
    {picture && <AvatarImage src={picture} alt="" />}
    <AvatarFallback className="text-[10px]">
      {(name ?? "?").trim().charAt(0).toUpperCase() || "?"}
    </AvatarFallback>
  </Avatar>
);

/**
 * What okthx is waiting on, in its own account of it — and how much of it
 * there is. Not the questions themselves: what a reader wants here is whether
 * to pick this up at all, which is what the questions are about rather than
 * what they are.
 */
const OpenQuestions = ({ count, why }: { count: number; why?: string }) => {
  if (count === 0) return null;
  return (
    <Section title="Task details">
      {/* As prose: okthx writes markdown, and a run of it showed its own
          asterisks. Unclamped, because a page is where the whole of it fits. */}
      {why && <p className="whitespace-pre-wrap text-sm">{toPlainText(why)}</p>}
      <p className="text-xs font-medium">{`${questionCount(count)} for you to answer`}</p>
    </Section>
  );
};

/**
 * What the reader wants for tasks of this kind, rather than for this one.
 *
 * Visible buttons here, where a row keeps them in its overflow: a row has no
 * room, and a page does. The phone's task screen spells them out for the same
 * reason. Below a rule, away from the task's own answers — these outlive the
 * task, and among its answers a rule read as a third way of saying no.
 */
const KindActions = ({ task }: { task: UserTask }) => {
  const ask = useAskAbout();
  const muteSender = useMuteSender();
  const [ruleOpen, setRuleOpen] = useState(false);
  const [pendingMute, setPendingMute] = useState<MuteChoice | null>(null);
  const ownDomains = useQuery(userTaskQueries.mutedSenders()).data?.ownDomains;
  const confirmation = pendingMute && muteConfirm(pendingMute);

  return (
    <div className="flex flex-col gap-3 border-t pt-4">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          data-statsig-name="tasks-detail-ask"
          disabled={ask.isPending}
          onClick={() => ask.mutate({ taskId: task.id })}
        >
          <MessageCircleMore className="size-3.5" />
          Ask Okthx about this
        </Button>
        {muteChoices(task.askedBy, ownDomains).map((choice) => (
          <Button
            key={choice.value}
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            data-statsig-name={
              choice.value.includes("@") ? "tasks-mute-sender" : "tasks-mute-domain"
            }
            onClick={() => setPendingMute(choice)}
          >
            <BellOff className="size-3.5" />
            {choice.label}
          </Button>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          data-statsig-name="tasks-detail-rule"
          onClick={() => setRuleOpen(true)}
        >
          <Signpost className="size-3.5" />
          {CREATE_RULE_LABEL}
        </Button>
      </div>
      {ruleOpen && (
        <TaskRulePanel
          taskId={task.id}
          onSaved={() => setRuleOpen(false)}
          onCancel={() => setRuleOpen(false)}
        />
      )}
      {/* A mute is permanent and reaches backwards, so it asks before it acts.
          The button names the action; this names who it is about, which is the
          part the button had no room for. */}
      <AlertDialog open={pendingMute !== null} onOpenChange={() => setPendingMute(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmation?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmation?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingMute) muteSender.mute(pendingMute.value);
                setPendingMute(null);
              }}
            >
              Mute
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/** One titled part of the page, as the project page heads its own. */
const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="flex flex-col gap-2">
    <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h2>
    {children}
  </section>
);

/**
 * Where the task came from, each line opening with what it is.
 *
 * No heading over the list: a source names itself, and one word for all of
 * them said less than each line already does.
 */
const Sources = ({ task }: { task: UserTask }) => {
  const links = sourceLinks(task.sources, task.threadId);
  if (links.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1">
      {links.map((link) => (
        <li key={link.key} className="flex items-center gap-2 text-sm">
          {/* The same mark the row's overflow gives a source, so a reader
              can tell two like-named mails apart by where they came from. */}
          <SourceMark icon={link.providerIcon} conversation={link.conversation} />
          {link.href ? (
            <Link
              href={link.href}
              className="link truncate"
              data-statsig-name="tasks-detail-source"
            >
              {link.label}
            </Link>
          ) : (
            <span className="truncate text-muted-foreground">{link.label}</span>
          )}
        </li>
      ))}
    </ul>
  );
};

/**
 * One task in full, at `/tasks/:id`.
 *
 * The same facts the card carries, with nothing clamped: a long title fits,
 * and the note is open rather than summarised. Its answers are the row's own
 * trio, not a pair — web's vocabulary for a task is the row, and a page that
 * offered two buttons where every list offers three would be a second way to
 * say the same things.
 *
 * In the Tasks pivot beside the project page, which is the same shape: a
 * drill-down that replaces the list and comes back to it. Not a route of its
 * own: `/tasks/:id` is already the task's link, and this is what it opens.
 */
export const TaskDetailView = ({
  taskId,
  accountSwitcher,
  leading,
}: {
  taskId: string;
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
}) => {
  const [, navigate] = useLocation();
  // The list the task was opened from, which the row's link carries along: a
  // label's list is a page of its own, and Back to the whole list from there
  // lost the place. A direct link carries nothing and goes to the list.
  const origin = useHistoryState<{ from?: unknown } | null>();
  const from = origin?.from;
  const backHref =
    typeof from === "string" && from.startsWith(userTaskHref()) ? from : userTaskHref();
  const setContext = useSetTaskContext();
  // Read from the list rather than fetched alone: every answer on this page
  // invalidates that one query, so the page cannot show a task the list has
  // already moved on from, and arriving costs no request the list had not
  // already made.
  const { data, isLoading, isError } = useQuery(userTaskQueries.list());
  const task = data?.tasks.find((candidate) => candidate.id === taskId);
  const questions = useOpenQuestions(task?.threadId);
  const chips = task?.labels ?? [];

  let body: ReactNode;
  if (isLoading) {
    body = <Loader className="py-8" />;
  } else if (!task) {
    // Only with nothing to show: a background re-read that fails must not
    // cover a task the list already holds.
    body = (
      <p className="p-4 text-sm text-muted-foreground">
        {isError ? "Couldn't load this task." : "That task is no longer on your list."}
      </p>
    );
  } else {
    const group = taskIndicatorParts(task);
    body = (
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {/* The same mark a row draws, in the same words: the detail is
                the row opened up, not a second description of the task. */}
            {group.key && <GroupMark group={group} />}
            {chips.map((label) => (
              <LabelChip key={label.id} label={label} statsigName="tasks-detail-label" />
            ))}
          </div>
          {/* Unclamped, where the card cuts it: a page is where the whole of a
              long title finally fits. */}
          <h1 className="text-xl font-semibold">{task.title}</h1>
          <DueLine task={task} />
          <TaskHolderLine task={task} questions={questions.count} />
        </div>
        <Sources task={task} />
        <OpenQuestions count={questions.count} why={questions.why} />
        <Section title="Notes">
          {/* Undriven, so the editor carries its own Add. A row hides that
              behind the pencil in its overflow because it has no room for a
              heading; a page has the heading, and a section that said nothing
              at all under it would be a dead one. */}
          <ContextEditor
            context={task.context}
            saving={setContext.isPending}
            onSave={(text) => setContext.mutateAsync({ taskId: task.id, text })}
            statsigName="tasks-detail-context"
          />
        </Section>
        {/* The row's own answers, so a task is answered the same way wherever
            it is met. As the page, not the row: the overflow's pencil, Ask,
            muting and rules are all spelled out on this page already, and
            drawn twice they would answer twice. */}
        <RowActions task={task} surface="page" />
        <KindActions task={task} />
      </div>
    );
  }

  return (
    <CanvasPivotPanel
      title={task?.title ?? "Task"}
      parentLabel="Tasks"
      onBack={() => navigate(backHref)}
      backStatsigName="tasks-detail-back"
      leading={leading}
      accountSwitcher={accountSwitcher}
    >
      {body}
    </CanvasPivotPanel>
  );
};
